'use strict'
import axios from 'axios/index'
import { delay } from '../utils/utils'

const defaultOptions = {
  params: {},
  minLength: 2,
  retryDelay: 500,
  token: '',
  baseUrl: 'https://adressevaelger.dk',
  adgangsadresserOnly: false,
  maksimum: 100,
  medtagForeloebige: false,
  renderCallback: () => {
    /* eslint no-console: 0 */
    console.error('No renderCallback supplied')
  },
  initialRenderCallback: () => {
    /* eslint no-console: 0 */
    console.error('No initialRenderCallback supplied')
  },
  selectCallback: () => {
    /* eslint no-console: 0 */
    console.error('No selectCallback supplied')
  },
  fetchImpl: (url, params) => axios.get(url, { params }).then(result => result.data)
}

const unwrapLookupResponse = (result) => {
  if (!result) {
    return null
  }

  if (result.adresse) {
    return result.adresse
  }

  if (Array.isArray(result.adresser) && result.adresser.length > 0) {
    return result.adresser[0]
  }

  return result
}

const mapLookupResponse = (result) => {
  const source = unwrapLookupResponse(result)

  if (!source) {
    return null
  }

  const husnummerObj = source.husnummer || source.Husnummer || null
  const postnummerObj = source.postnummer || source.Postnummer || husnummerObj?.postnummer || husnummerObj?.Postnummer || null

  const postnr = source.postnr || postnummerObj?.postnr || null
  const postdistrikt = source.postdistrikt || postnummerObj?.postdistrikt || postnummerObj?.navn || null
  const vejnavn = source.vejnavn || husnummerObj?.vejnavn || source.NavngivenVej?.navn || null
  const husnummer = typeof source.husnummer === 'string' ? source.husnummer : (husnummerObj?.husnummertekst || null)
  const etagebetegnelse = source.etagebetegnelse || source.Etagebetegnelse || null
  const doerbetegnelse = source.doerbetegnelse || source.Doerbetegnelse || null
  const titel = source.titel || source.adressebetegnelse || [vejnavn, husnummer, postnr, postdistrikt].filter(Boolean).join(' ')

  return Object.assign({}, source, {
    type: source.type || 'adresse',
    id: source.id || source.id_lokalid || source.id_lokalId || source.idLokalId || null,
    titel,
    vejnavn,
    husnummer,
    postnr,
    postdistrikt,
    etagebetegnelse,
    doerbetegnelse
  })
}

const normalizeSearchResponse = (result) => {
  if (Array.isArray(result)) {
    return result
  }

  if (Array.isArray(result?.fund)) {
    return result.fund
  }

  if (Array.isArray(result?.results)) {
    return result.results
  }

  if (Array.isArray(result?.items)) {
    return result.items
  }

  return []
}

export class AddressSelectorService {
  constructor (options, callback) {
    options = Object.assign({}, defaultOptions, options || {})
    this.options = options
    this._requestSeq = 0
    this.setInitialRenderCallback(callback)
    this.setRenderCallback(callback)
    this.setSelectCallback(callback)
  }

  _searchEndpoint () {
    return this.options.adgangsadresserOnly ? '/husnumre/soeg' : '/adresser/soeg'
  }

  _getToken () {
    return this.options.token || ''
  }

  _search (text) {
    const token = this._getToken()
    const params = Object.assign({}, this.options.params, {
      tekst: text,
      maksimum: this.options.maksimum,
      medtagforeloebige: this.options.medtagForeloebige,
      fuzzy: '',
      ...(token ? { token } : {})
    })

    return this.options.fetchImpl(`${this.options.baseUrl}${this._searchEndpoint()}`, params)
  }

  _getById (id) {
    const token = this._getToken()
    const params = token ? { token } : {}
    return this.options.fetchImpl(`${this.options.baseUrl}/adresser/${id}`, params)
  }

  _handleRequestError (label, error, fallback) {
    console.error(`AddressSelector request failed: ${label}`, error)
    return delay(this.options.retryDelay).then(() => fallback())
  }

  setRenderCallback (renderCallback) {
    this.options.renderCallback = renderCallback
  }

  setInitialRenderCallback (renderCallback) {
    this.options.initialRenderCallback = renderCallback
  }

  setSelectCallback (selectCallback) {
    this.options.selectCallback = selectCallback
  }

  update (text) {
    if (!text || text.length < this.options.minLength) {
      this.options.renderCallback([])
      return
    }

    const seq = ++this._requestSeq
    const render = (result) => {
      if (seq === this._requestSeq) {
        this.options.renderCallback(normalizeSearchResponse(result))
      }
    }

    this._search(text)
      .then(result => render(result))
      .catch(error => {
        this._handleRequestError('search', error, () => this._search(text)
          .then(result => render(result))
          .catch(secondError => {
            console.error('AddressSelector retry failed: search', secondError)
            if (seq === this._requestSeq) {
              this.options.renderCallback([])
            }
          }))
      })
  }

  select (item) {
    if (!item) {
      return
    }

    if (item.type === 'adresse' && item.id) {
      this._getById(item.id)
        .then(result => {
          const mapped = mapLookupResponse(result)
          if (mapped) {
            this.options.selectCallback(mapped)
          }
        })
        .catch(error => {
          this._handleRequestError('lookup', error, () => this._getById(item.id)
            .then(result => {
              const mapped = mapLookupResponse(result)
              if (mapped) {
                this.options.selectCallback(mapped)
              }
            })
            .catch(secondError => {
              console.error('AddressSelector retry failed: lookup', secondError)
            }))
        })
      return
    }

    const nextText = item.titel || item.tekst || ''
    this.update(nextText)
  }

  selectInitial (id) {
    if (!id) {
      return
    }

    this._getById(id)
      .then(result => {
        const mapped = mapLookupResponse(result)
        if (mapped) {
          this.options.initialRenderCallback([mapped])
        }
      })
      .catch(error => {
        this._handleRequestError('initial lookup', error, () => this._getById(id)
          .then(result => {
            const mapped = mapLookupResponse(result)
            if (mapped) {
              this.options.initialRenderCallback([mapped])
            }
          })
          .catch(secondError => {
            console.error('AddressSelector retry failed: initial lookup', secondError)
            this.options.initialRenderCallback([])
          }))
      })
  }
}